import React from 'react';
import { ACTION_ICON_MAP } from './treeMenuIcons';
import { ContextMenuAction } from '../../models/TreeModel';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from '../../../../components/ui/context-menu';
import { REASON_MENU_ITEM_CLASS } from '../../../../components/ui/reasonMenuItem';


function ActionIcon({ action }: { action: ContextMenuAction }) {
  if (action.icon && ACTION_ICON_MAP[action.icon]) {
    const IconComponent = ACTION_ICON_MAP[action.icon];
    return <IconComponent className="mr-2 h-4 w-4 shrink-0" />;
  }
  // F4 (last UI/UX pass): a glyph name the map lacks keeps the label in line with its neighbours (it used to render
  // nothing and shift the label 24 px left). An item that asks for no glyph renders none, as before.
  if (action.icon) return <span className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" data-testid="tree-menu-icon-spacer" />;
  return null;
}

/**
 * The inside of one item (UI/UX batch C2 N7 / N16): the icon, the label and — for an unavailable item — its reason as a
 * visible second line (it used to live only in the `title`, which keyboard users never see), like the storage row and
 * More menus (`StorageMenuItemContent`, whose reason line batch C1 set to `text-xs` — the same size here). Every
 * engine's tree menu renders through it.
 */
function ActionContent({ action }: { action: ContextMenuAction }) {
  const reason = action.disabled ? action.disabledReason : undefined;
  if (!reason) {
    return (
      <>
        <ActionIcon action={action} />
        {action.label}
      </>
    );
  }
  return (
    <span className="flex items-start">
      <ActionIcon action={action} />
      <span className="flex min-w-0 flex-col">
        <span>{action.label}</span>
        {/* Last UI/UX pass (Z4 / F1): prose wraps at spaces; an unbroken permission path breaks only where it must. */}
        <span className="max-w-[18rem] text-xs leading-snug text-muted-foreground [overflow-wrap:anywhere]" data-testid="tree-menu-item-reason">{reason}</span>
      </span>
    </span>
  );
}

/** A destructive action (Delete share…, Delete table) reads red, as in the storage row menus; never when unavailable. */
const DESTRUCTIVE_ITEM_CLASS = 'text-destructive focus:text-destructive [&_svg]:text-destructive';

const SEPARATOR: ContextMenuAction = { id: 'separator', label: '', isSeparator: true };

/**
 * C2 N16: destructive items form the LAST group of the menu (after a separator), whatever order the contributors
 * composed them in — the storage row and More menus' rule ("Delete comes last"). Separators left leading, trailing or
 * doubled by the move are dropped. Only items flagged `destructive` move, so a menu without one is returned as is.
 */
export function orderDestructiveLast(actions: readonly ContextMenuAction[]): ContextMenuAction[] {
  const destructive = actions.filter((action) => action.destructive && !action.isSeparator);
  if (destructive.length === 0) return [...actions];
  const rest = actions.filter((action) => !destructive.includes(action));
  const tidy = (list: ContextMenuAction[]) => list.filter((action, index) =>
    !action.isSeparator || (index > 0 && index < list.length - 1 && !list[index - 1].isSeparator));
  return tidy([...tidy(rest), SEPARATOR, ...destructive]);
}

interface TreeNodeContextMenuProps {
  actions: ContextMenuAction[];
  /**
   * Last UI/UX pass (Codex Z3 / Opus F1): rebuilds the items each time the menu OPENS (right-click, Shift+F10, the Menu
   * key — Radix opens on the `contextmenu` event all three raise). For menus whose availability lives in a store the
   * memoised row does not subscribe to (a remembered table-admin refusal): the row may not have re-rendered since.
   */
  refreshActions?: () => ContextMenuAction[];
  getActionHandler: (actionId: string) => (e: Event) => void;
  children: React.ReactNode;
}

export function TreeNodeContextMenu({
  actions,
  refreshActions,
  getActionHandler,
  children,
}: TreeNodeContextMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [openedActions, setOpenedActions] = React.useState<ContextMenuAction[] | null>(null);
  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
    setOpenedActions(open && refreshActions ? refreshActions() : null);
  };
  const shownActions = openedActions ?? actions;
  const getActionTestId = (actionId: string) => (isOpen ? `context-menu-${actionId}` : undefined);

  return (
    // `modal={false}` is load-bearing for perf, not a UX preference. Radix's
    // modal mode mounts `RemoveScroll` on open, which injects a stylesheet and
    // writes `pointer-events: none` onto `<body>` — both invalidate style for
    // the WHOLE document. The Object Explorer tree is not virtualized, so on a
    // real tree (~1,000 expanded rows) that forced recalc + relayout cost
    // ~130ms of browser work between the right-click and the menu painting.
    // Non-modal keeps outside-click dismissal (DismissableLayer) and costs
    // only the loss of scroll-locking behind the open menu.
    <ContextMenu modal={false} onOpenChange={onOpenChange}>
      <ContextMenuTrigger>{children}</ContextMenuTrigger>
      {/* Z4: never wider than the room Radix measured beside the pointer. */}
      <ContextMenuContent className="max-w-[var(--radix-context-menu-content-available-width)]" data-testid={isOpen ? 'context-menu' : undefined}>
        {orderDestructiveLast(shownActions).map((action, actionIndex) => {
          if (action.isSeparator) {
            return (
              <ContextMenuSeparator
                key={`${action.id}-${actionIndex}`}
              />
            );
          }

          if (action.submenu && action.submenu.length > 0) {
            return (
              <ContextMenuSub key={action.id}>
                <ContextMenuSubTrigger data-testid={getActionTestId(action.id)}>
                  <ActionIcon action={action} />
                  {action.label}
                </ContextMenuSubTrigger>
                <ContextMenuSubContent>
                  {action.submenu.map((subAction, subActionIndex) => {
                    if (subAction.isSeparator) {
                      return (
                        <ContextMenuSeparator
                          key={`${action.id}-${subAction.id}-${subActionIndex}`}
                        />
                      );
                    }

                    // Same WHY-tooltip convention as the top-level items below —
                    // stay pointer-events-enabled (Radix's `disabled` blocks
                    // hover/title) and guard `onSelect` instead.
                    if (subAction.disabled) {
                      return (
                        <ContextMenuItem
                          key={subAction.id}
                          onSelect={(e) => e.preventDefault()}
                          title={subAction.disabledReason}
                          aria-disabled="true"
                          className={REASON_MENU_ITEM_CLASS}
                          data-testid={getActionTestId(subAction.id)}
                          data-disabled-reason={subAction.disabledReason}
                        >
                          <ActionContent action={subAction} />
                        </ContextMenuItem>
                      );
                    }

                    return (
                      <ContextMenuItem
                        key={subAction.id}
                        onSelect={getActionHandler(subAction.id)}
                        className={subAction.destructive ? DESTRUCTIVE_ITEM_CLASS : undefined}
                        data-testid={getActionTestId(subAction.id)}
                      >
                        <ActionContent action={subAction} />
                      </ContextMenuItem>
                    );
                  })}
                </ContextMenuSubContent>
              </ContextMenuSub>
            );
          }

          // Repo convention (feedback_vqe_disabled_buttons_need_tooltip) — a
          // disabled item must still explain why on hover. Radix's own
          // `disabled` prop applies `pointer-events-none`, which also blocks
          // hover (and therefore a native `title` tooltip) — so disabled
          // items stay pointer-events-enabled, are styled muted manually, and
          // guard `onSelect` instead of relying on Radix's disabled state.
          if (action.disabled) {
            return (
              <ContextMenuItem
                key={action.id}
                onSelect={(e) => e.preventDefault()}
                title={action.disabledReason}
                aria-disabled="true"
                className={REASON_MENU_ITEM_CLASS}
                data-testid={getActionTestId(action.id)}
                data-disabled-reason={action.disabledReason}
              >
                <ActionContent action={action} />
              </ContextMenuItem>
            );
          }

          return (
            <ContextMenuItem
              key={action.id}
              onSelect={getActionHandler(action.id)}
              className={action.destructive ? DESTRUCTIVE_ITEM_CLASS : undefined}
              data-testid={getActionTestId(action.id)}
            >
              <ActionContent action={action} />
            </ContextMenuItem>
          );
        })}
      </ContextMenuContent>
    </ContextMenu>
  );
}
